import { UserMgtService } from '../../shared/services/user-management.service';
import { APPNAME } from '../../app.GlobalSettings';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AuthService } from '../../shared/services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { dlgUserInfo, UserManagementUserComponent } from './../content/system-maintenance/user-management-user/user-management-user.component';
import { debounceTime, take } from 'rxjs/operators';
import { SearchService } from 'app/shared/services/search.service';
import { ReplaySubject, Subscription } from 'rxjs';
import { MessageBoxComponent, MessageBoxParameters, MessageBoxType } from 'app/components/messageBox/messageBox.component';
import { FeedbackComponent } from 'app/components/dlgFeedback/dlgFeedback.component';
import { SubjectStopwatchService } from 'app/shared/services/subject-stopwatch/subject-stopwatch.service';
import { messageService } from 'app/shared/services/messages/message.module';
import { DefaultDataService } from 'app/shared/services/default-data.service';
import { UserService } from 'app/shared/services/user.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'fuse-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
})

export class FuseToolbarComponent implements OnInit, OnDestroy {
  userStatusOptions: any[];
  languages: any;
  selectedLanguage: any;
  showLoadingBar: boolean;
  horizontalNav: boolean;
  noNav: boolean;
  userLoggedIn = false;
  appName = 'Phoenix';

  userName = '';
  userInitials = '';
  searchTerm = '';

  hours = 0;
  minutes = 0;
  seconds = 0;

  paused = false;
  stopped = false;

  showStopwatch = false;

  searchTermEntered: ReplaySubject<string> = new ReplaySubject<string>(1, 100);
  onSearchResult$: Subscription;
  onSearchTermEntered$: Subscription;
  tick$: Subscription;
  paused$: Subscription;
  resume$: Subscription;
  stopped$: Subscription;
  showStopwatch$: Subscription;

  @ViewChild('searchbar') searchBar;

  constructor(
    private router: Router,
    private fuseConfig: FuseConfigService,
    private sidebarService: FuseSidebarService,
    private searchService: SearchService,
    private defaultDataService: DefaultDataService,
    private userService: UserService,
    private auth: AuthService,
    public dialog: MatDialog,
    public userMgtService: UserMgtService,
    private subjectStopWatch: SubjectStopwatchService,
    @Inject(APPNAME) appName: string,
  ) {
    this.auth.userName().subscribe((d) => {
      this.userName = d;
    });

    this.appName = appName;

    this.userStatusOptions = [
      {
        title: 'Online',
        icon: 'icon-checkbox-marked-circle',
        color: '#4CAF50',
      },
      {
        title: 'Away',
        icon: 'icon-clock',
        color: '#FFC107',
      },
      {
        title: 'Do not Disturb',
        icon: 'icon-minus-circle',
        color: '#F44336',
      },
      {
        title: 'Invisible',
        icon: 'icon-checkbox-blank-circle-outline',
        color: '#BDBDBD',
      },
      {
        title: 'Offline',
        icon: 'icon-checkbox-blank-circle-outline',
        color: '#616161',
      },
    ];

    this.languages = [
      {
        id: 'en',
        title: 'English',
        flag: 'us',
      },
      {
        id: 'tr',
        title: 'Turkish',
        flag: 'tr',
      },
    ];

    this.selectedLanguage = this.languages[0];

    router.events.subscribe(
      (event) => {
        if (event instanceof NavigationStart) {
          this.showLoadingBar = true;
        }
        if (event instanceof NavigationEnd) {
          this.showLoadingBar = false;
        }
        if (event instanceof NavigationCancel) {
          this.showLoadingBar = false;
        }
      });

    this.fuseConfig.onConfigChanged.subscribe((settings) => {
      this.horizontalNav = settings.layout.navigation === 'top';
      this.noNav = settings.layout.navigation === 'none';
    });

  }

  ngOnInit() {
    this.onSearchResult$ = this.searchService.onSearchResult.subscribe((result) => {
      if (this.searchTerm) {
        if (this.searchBar) {
          this.searchBar.collapsed = true;
        }
        if (result) {
          if (result.length > 0) {
            if (result.length === 1) {
              const url = result[0].url;
              if (url) {
                this.router.navigateByUrl(url);
              }
            }
            else {
              // navigate to search results page
              this.router.navigateByUrl('/searchresults/' + encodeURIComponent(this.searchService.searchTerm));
            }
          }
          else {
            const info = new MessageBoxParameters(`There are no records matching the search term "${this.searchService.searchTerm}"`, 'Search Results', MessageBoxType.Ok);
            this.dialog.open(MessageBoxComponent, { data: info });
          }
        }
      }
    });

    this.onSearchTermEntered$ = this.searchTermEntered.pipe(
      debounceTime(200),
    ).subscribe((searchTerm) => {
      this.searchTerm = searchTerm;
    });

    this.showStopwatch$ = this.subjectStopWatch.onShow.subscribe((val) => {
      this.showStopwatch = val;
    });

    this.tick$ = this.subjectStopWatch.onTick.subscribe((ticks) => {
      this.hours = Math.floor(ticks / 3600);
      this.minutes = Math.floor((ticks % 3600) / 60);
      this.seconds = Math.floor((ticks % 3600) % 60);
    });

    this.paused$ = this.subjectStopWatch.onPaused.subscribe(() => { this.paused = true; });

    this.stopped$ = this.subjectStopWatch.onStopped.subscribe(() => { this.stopped = true; });

    this.resume$ = this.subjectStopWatch.onResume.subscribe(() => { this.paused = false; this.stopped = false; });
  }

  ngOnDestroy() {
    this.onSearchResult$.unsubscribe();
    this.onSearchTermEntered$.unsubscribe();
    if (this.tick$) { this.tick$.unsubscribe(); }
    if (this.paused$) { this.paused$.unsubscribe(); }
    if (this.stopped$) { this.stopped$.unsubscribe(); }
    if (this.resume$) { this.resume$.unsubscribe(); }
    if (this.showStopwatch$) { this.showStopwatch$.unsubscribe(); }
  }

  toggleSidebarOpened(key) {
    this.sidebarService.getSidebar(key).toggleOpen();
  }

  doSearch() {
    if (this.searchTerm) {
      const currentUser = this.userService.getCurrentUser();
      const includeArchived = this.defaultDataService.includeArchivedSubjectsInSearches && (currentUser.isAdmin || currentUser.canViewArchive);
      this.searchService.doSearch(this.searchTerm, '', includeArchived);
    }
  }

  search(value) {
    this.searchTermEntered.next(value);
  }

  showFeedbackForm() {
    this.pauseStopwatch();
    this.dialog.open(FeedbackComponent).afterClosed().pipe(take(1)).subscribe(() => {
      this.resumeStopwatch();
    });
  }

  // setLanguage(lang) {
  //   // Set the selected language for toolbar
  //   this.selectedLanguage = lang;

  //   // Use the selected language for translations
  //   this.translate.use(lang.id);
  // }

  logIn() {
    this.auth.startAuthentication(false);
  }

  logOut() {

    // Stop the stopwatch when logout is clicked    
    if (this.subjectStopWatch.running)    
      this.subjectStopWatch.stop(true,true);  
    else 
      this.auth.logout();    
  }

  showUserProfile() {
    this.pauseStopwatch();

    const email = this.auth.getClaim('email').toLowerCase();

    // Setup a hander that will fire when Users are obtained from webapi, and then un-subscribe when done.
    // This will take the first data push from the userMgr service and then complete the subscription.
    this.userMgtService.onChanged.pipe(take(1)).subscribe((userList) => {
      if (userList && userList.constructor === Array) {

        // We have users so find current logged in user
        const currentUser = userList.find((x) => x.loginId.toLowerCase() === email);

        // if we find current user then show dialog to allow user to edit there details.
        if (currentUser != null) {
          const dlgInfo = new dlgUserInfo();
          dlgInfo.userInfo = currentUser;
          dlgInfo.dlgTitle = 'My Profile';
          this.dialog.open(UserManagementUserComponent, { data: dlgInfo }).afterClosed().subscribe((d) => {
            this.resumeStopwatch();
          });
        }
      }
    });
    // this will cause the above code to fire once the data has been obtained.
    this.userMgtService.getUsers();
  }

  getUserName(): string {
    return this.auth.getUserName();
  }

  getUserNameInitials(): string {
    return this.auth.getUserNameInitials();
  }

  pause(e) {
    this.subjectStopWatch.pause();
  }

  stop(e) {
    this.subjectStopWatch.stop();
  }

  resume(e) {
    this.subjectStopWatch.resume();
  }

  pauseStopwatch() {
    // if stopwatch is displayed then pause it and display message
    if (this.showStopwatch) {
      this.subjectStopWatch.pause();
      messageService.info('Stopwatch has been paused', 'Subject Stopwatch');
    }
  }

  resumeStopwatch() {
    if (this.showStopwatch) {
      this.subjectStopWatch.resume();
      messageService.info('Stopwatch has been resumed', 'Subject Stopwatch');
    }
  }
}
